import React, { useState, useEffect, useCallback } from 'react';
import {
  Layout, Card, Button, Space, Typography, message, Tag, Tooltip, 
  Table, Input, Statistic, Row, Col, Modal
} from 'antd';
import {
  FileExcelOutlined, EyeOutlined, CalendarOutlined,
  ShoppingOutlined, ReloadOutlined, DeleteOutlined, FilterOutlined,
  ClearOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import SalesReceiptModal from './SalesReceiptModal';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import './SalesReport.css';

const { Text } = Typography;
const { Content } = Layout;
const { Search } = Input;

const SalesReport = () => {
  const { t } = useTranslation();
  const [salesTransactions, setSalesTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
  moment().startOf('month'),
  moment().endOf('month')
]);
  const [csvData, setCsvData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [paymentTransaction, setPaymentTransaction] = useState(null);
  const [newPaymentMethod, setNewPaymentMethod] = useState('');
  const [newBankDetailId, setNewBankDetailId] = useState(null);
  const [newPaymentDate, setNewPaymentDate] = useState('');
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [bankDetails, setBankDetails] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  const fetchSalesTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/sales/sales-transactions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSalesTransactions(data.data);
      setFilteredTransactions(data.data);
      updateCsvData(data.data);
    } catch (error) {
      message.error('Failed to fetch sales transactions');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);


const fetchBankDetails = useCallback(async () => {
  try {
    setLoadingBanks(true);
    const token = localStorage.getItem('token');

    const { data } = await axios.get(`${API_URL}/bank-details`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    setBankDetails(data.data || data);  // supports both response formats
  } catch (error) {
    console.error('Error fetching bank details:', error);
    message.error('Failed to fetch bank details');
  } finally {
    setLoadingBanks(false);
  }
}, [API_URL]);


  
useEffect(() => {
  fetchSalesTransactions();
  fetchBankDetails();  
}, [fetchSalesTransactions, fetchBankDetails]);


  const handleShowReceipt = (transaction) => {
  if (transaction.payment_method === "unpaid") {
    return message.error("Cannot print receipt. This transaction is unpaid.");
  }
  setSelectedTransaction(transaction);
};

  const formatCurrency = (value) => 
    parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const updateCsvData = (transactions) => {
  const formattedData = transactions.map(t => ({
    transaction_id: t.transaction_id,
    document_no: t.document_no,
    student_name: `${t.student_firstname} ${t.student_lastname}`,
    product_ids: t.items.map(item => item.product_id).filter(Boolean).join(', '),
    items: t.items.map(item => item.item_name).join(', '),
    total_amount: formatCurrency(t.total_amount),
    discount: formatCurrency(t.discount),
    vat: formatCurrency(t.vat),
    final_amount: formatCurrency(t.final_amount),
    payment_method: t.payment_method,
    bank_name: t.payment_method === 'bank_transfer' ? (t.bank_name || 'N/A') : '-',
    account_name: t.payment_method === 'bank_transfer' ? (t.account_name || 'N/A') : '-',
    account_number: t.payment_method === 'bank_transfer' ? (t.account_number || 'N/A') : '-',
    branch: t.payment_method === 'bank_transfer' ? (t.branch || 'N/A') : '-',
    bank_detail_id: t.payment_method === 'bank_transfer' ? (t.bank_detail_id || 'N/A') : '-',
    created_by: t.created_by,
    transaction_date: new Date(t.transaction_date).toLocaleString(),
    payment_date: t.payment_date || '',
    note: t.note || '',
  }));
  setCsvData(formattedData);
};
  const handleDateRangeChange = (index, date) => {
    const newDateRange = [...dateRange];
    newDateRange[index] = date ? moment(date) : null;
    setDateRange(newDateRange);
  };

 const applyFilters = () => {
  let filtered = [...salesTransactions];

  if (dateRange[0] && dateRange[1]) {
    const startDate = dateRange[0].startOf('day');
    const endDate = dateRange[1].endOf('day');

    filtered = filtered.filter(t => {
      const transactionDate = moment(t.transaction_date);
      return (
        transactionDate.isSameOrAfter(startDate) &&
        transactionDate.isSameOrBefore(endDate)
      );
    });
  }

  if (searchText) {
    const searchLower = searchText.toLowerCase();

    filtered = filtered.filter(t =>
      t.document_no?.toLowerCase().includes(searchLower) ||

      `${t.student_firstname} ${t.student_lastname}`
        .toLowerCase()
        .includes(searchLower) ||

      // ✅ student nickname (NEW)
      (t.student_nickname &&
        t.student_nickname.toLowerCase().includes(searchLower)) ||

      t.payment_method?.toLowerCase().includes(searchLower) ||

      (typeof t.created_by === 'string' &&
        t.created_by.toLowerCase().includes(searchLower)) ||

      t.items?.some(item =>
        item.item_name?.toLowerCase().includes(searchLower)
      )
    );
  }

  if (paymentMethodFilter) {
    filtered = filtered.filter(
      t => t.payment_method === paymentMethodFilter
    );
  }

  setFilteredTransactions(filtered);
  updateCsvData(filtered);
};


  useEffect(() => {
  applyFilters();
}, [dateRange, searchText, salesTransactions]);

const openPaymentModal = (transaction) => {
  setPaymentTransaction(transaction);
  setNewPaymentMethod(transaction.payment_method);
  setNewBankDetailId(transaction.bank_detail_id || null);
  // payment_date arrives as a plain 'YYYY-MM-DD' string, which is exactly what
  // a native date input expects.
  setNewPaymentDate(transaction.payment_date || '');
  setPaymentModalVisible(true);
};

const updatePaymentMethod = async () => {
  if (!paymentTransaction) return;

  try {
    setUpdatingPayment(true);
    const token = localStorage.getItem('token');

    await axios.put(
      `${API_URL}/sales/update-payment-method/${paymentTransaction.transaction_id}`,
      {
        new_payment_method: newPaymentMethod,
        bank_detail_id: newPaymentMethod === 'bank_transfer' ? newBankDetailId : null,
        // Blank means "leave as is" server-side; unpaid clears it regardless.
        payment_date: newPaymentDate || null
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    message.success("Payment method updated");

    // CLOSE MODAL
    setPaymentModalVisible(false);
    setPaymentTransaction(null);

    // 🔥 AUTO-REFRESH TABLE
    fetchSalesTransactions();
  } catch (err) {
    console.error(err);
    message.error("Failed to update payment method");
  } finally {
    setUpdatingPayment(false);
  }
};


  const resetFilters = () => {
  const defaultRange = [
    moment().startOf('month'),
    moment().endOf('month')
  ];
  setDateRange(defaultRange);
  setSearchText('');
  setFilteredTransactions(salesTransactions);
  updateCsvData(salesTransactions);
};

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/sales/delete-sale/${transactionToDelete}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { password: passwordInput }
      });
      
      message.success('Sale transaction deleted successfully');
      setDeleteModalVisible(false);
      setPasswordInput('');
      setTransactionToDelete(null);
      
      // Refresh the data
      fetchSalesTransactions();
    } catch (error) {
      console.error('Error deleting sale:', error);
      if (error.response?.status === 401) {
        message.error('Invalid password');
      } else if (error.response?.status === 403) {
        message.error('Access denied: Only superadmins can delete sales transactions');
      } else {
        message.error('Failed to delete sale transaction');
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const showDeleteModal = (transactionId) => {
    setTransactionToDelete(transactionId);
    setDeleteModalVisible(true);
  };

  const paymentMethodLabels = {
  cash: 'Cash',
  bank_transfer: 'Bank Transfer',
  credit_card: 'Credit Card',
  unpaid: '❗Unpaid'
};

  const getPaymentMethodColor = (method) => ({
    cash: 'green',
    unpaid: 'red',
    credit_card: 'purple',
    bank_transfer: 'cyan'
  }[method?.toLowerCase()] || 'default');

const totalPaid = filteredTransactions
  .filter(t => t.payment_method !== 'unpaid')
  .reduce((sum, t) => sum + parseFloat(t.final_amount), 0);

const totalUnpaid = filteredTransactions
  .filter(t => t.payment_method === 'unpaid')
  .reduce((sum, t) => sum + parseFloat(t.final_amount), 0);

const totalTransactions = filteredTransactions.length;

const totalSales = filteredTransactions.reduce(
  (sum, t) => sum + parseFloat(t.final_amount || 0),
  0
);



  const columns = [
  { title: 'id', dataIndex: 'transaction_id', key: 'transaction_id', width: 40 },
 {
  title: 'Dates',
  key: 'dates',
  width: 160,
  sorter: (a, b) => new Date(a.transaction_date) - new Date(b.transaction_date),
  defaultSortOrder: 'descend',
  render: (_, record) => (
    <div style={{ lineHeight: '1.2' }}>
      <div><strong>{moment(record.transaction_date).format('DD-MMM-YY HH:mm')}</strong></div>
      {/* Three states: genuinely unpaid, paid on a known date, or paid with no
          date recorded (the POS date is optional). Parsed with an explicit format
          so the date-only value is not shifted by timezone. */}
      {record.payment_method === 'unpaid' ? (
        <div style={{ fontSize: '11px', color: '#cf1322' }}>unpaid</div>
      ) : record.payment_date ? (
        <div style={{ fontSize: '11px', color: '#389e0d' }}>
          Paid: {moment(record.payment_date, 'YYYY-MM-DD').format('DD-MMM-YY')}
        </div>
      ) : (
        <div style={{ fontSize: '11px', color: '#d46b08' }}>Paid: no date</div>
      )}
      <div style={{ fontSize: '11px', color: '#888' }}>
        Updated: {moment(record.updated_at).format('DD-MMM-YY HH:mm')}
      </div>
    </div>
  )
},
{ 
  title: 'Receipt', 
  dataIndex: 'document_no', 
  key: 'document_no', 
  width: 120,
  render: (text, record) => {

    const isUnpaid = record.payment_method === 'unpaid';

    return (
      <Tooltip title={isUnpaid ? "Cannot view receipt for unpaid transaction" : ""}>
        <Button 
          type="link" 
          icon={<EyeOutlined />}
          disabled={isUnpaid}
          onClick={() => {
            if (!isUnpaid) handleShowReceipt(record);
          }}
          style={{
            color: isUnpaid ? '#aaa' : undefined,
            cursor: isUnpaid ? 'not-allowed' : 'pointer'
          }}
        >
          {text}
        </Button>
      </Tooltip>
    );
  },
},

  { 
    title: 'Student', 
    key: 'student', 
    width: 220, 
    render: (_, r) => `${r.student_firstname} ${r.student_lastname} (${r.student_nickname})`, 
    ellipsis: true 
  },
  {
    title: 'Product ID',
    key: 'product_id',
    width: 110,
    render: (_, r) => {
      // One transaction can hold several products, so this lists every id.
      const ids = (r.items || []).map(i => i.product_id).filter(Boolean);
      if (!ids.length) return '-';
      return ids.length > 2 ? (
        <Tooltip title={ids.join(', ')}>
          {ids.slice(0, 2).join(', ')}... +{ids.length - 2}
        </Tooltip>
      ) : ids.join(', ');
    },
    ellipsis: true
  },
  { 
    title: 'Items', 
    key: 'items', 
    width: 100, 
    render: (_, r) => {
      const items = r.items || [];
      return items.length > 2 ? (
        <Tooltip title={items.map(i => i.item_name).join(', ')}>
          {items.slice(0, 2).map(i => i.item_name).join(', ')}... +{items.length - 2}
        </Tooltip>
      ) : items.map(i => i.item_name).join(', ') || 'No items';
    }, 
    ellipsis: true 
  },
 {
  title: 'Amounts',
  key: 'amounts',
  width: 160,
  align: 'right',
  sorter: (a, b) => a.final_amount - b.final_amount,
  render: (_, record) => (
    <div style={{ textAlign: 'right', lineHeight: '1.1' }}>
      {/* Final Amount Highlighted */}
      <div style={{ fontWeight: 'bold', color: '#1677ff' }}>
        {formatCurrency(record.final_amount)}
      </div>

      {/* Total */}
      <div style={{ fontSize: '11px', color: '#555' }}>
        Total: {formatCurrency(record.total_amount)}
      </div>

      {/* Discount */}
      <div style={{ fontSize: '11px', color: '#888' }}>
        Disc: {formatCurrency(record.discount)}
      </div>

      {/* VAT */}
      <div style={{ fontSize: '11px', color: '#888' }}>
        VAT: {formatCurrency(record.vat)}
      </div>
    </div>
  )
},
  { 
    title: 'Payment', 
    dataIndex: 'payment_method', 
    key: 'payment_method', 
    width: 130, 
    render: m => (
  <Tag color={getPaymentMethodColor(m)}>
    {paymentMethodLabels[m] || m}
  </Tag>
),
  },
  {
    title: 'Bank Details',
    key: 'bank_details',
    width: 120,
    render: (_, record) => {
      if (record.payment_method === 'bank_transfer' && record.bank_detail_id) {
        return (
          <div>
            <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
              {record.bank_name || 'N/A'}
            </div>
            <div style={{ fontSize: '11px', color: '#666' }}>
              {record.account_name || 'N/A'}
            </div>
            <div style={{ fontSize: '10px', color: '#999' }}>
              {record.account_number || 'N/A'}
            </div>
          </div>
        );
      }
      return <Text type="secondary">-</Text>;
    },
    ellipsis: true,
  },
  {
    title: 'By',
    dataIndex: 'created_by_nickname', 
    key: 'created_by',
    width: 100,
    render: (text) => text || '-', 
  },    
  {
  title: 'Note',
  dataIndex: 'note',
  key: 'note',
  width: 200,
  render: (t) => t || '-',
  ellipsis: true
},

  {
  title: 'Actions',
  key: 'actions',
  width: 150,
  render: (_, record) => (
    <Space>
      <Button
        type="default"
        size="small"
        onClick={() => openPaymentModal(record)}
      >
        Change Payment
      </Button>

      <Button
        type="primary"
        danger
        icon={<DeleteOutlined />}
        size="small"
        onClick={() => showDeleteModal(record.transaction_id)}
      />
    </Space>
  ),
}

];

  const getCSVFilename = () => {
    let filename = 'sales_report';
    if (dateRange[0] && dateRange[1]) {
      filename += `_${dateRange[0].format('YYYYMMDD')}_to_${dateRange[1].format('YYYYMMDD')}`;
    } else {
      filename += `_${moment().format('YYYYMMDD')}`;
    }
    return `${filename}.csv`;
  };

  return (
    <Layout className="sales-report-layout">
      <Sidebar />
      <Layout>
        <Header />
        <Content className="sales-report-content">
          <Card className="dashboard-card">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <Card hoverable className="summary-card">
                    <Statistic
                      title="Total Transactions"
                      value={totalTransactions}
                      prefix={<ShoppingOutlined />}
                    />
                  </Card>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Card hoverable className="summary-card">
                    <Statistic
                      title="Paid Amount"
                      value={formatCurrency(totalPaid)}
                      prefix="฿"
                    />
                  </Card>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Card hoverable className="summary-card">
                    <Statistic
                      title="Unpaid Amount"
                      value={formatCurrency(totalUnpaid)}
                      prefix="฿"
                      valueStyle={{ color: '#cf1322' }} // red text
                    />
                  </Card>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <Card hoverable className="summary-card">
                    <Statistic
                      title="Total Sales"
                      value={formatCurrency(totalSales)}
                      prefix="฿"
                    />
                  </Card>
                </Col>
              </Row>


          </Card>
          <Card className="filters-card">
                <Row gutter={[16, 16]} align="middle">
                  <Col xs={24} md={8} lg={8}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Text strong><CalendarOutlined /> Date Range</Text>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {/* From Date */}
                        <span>From:</span>
                        <div>
                          <input
                            type="date"
                            id="hidden-from-date"
                            value={dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : ''}
                            onChange={(e) => handleDateRangeChange(0, e.target.value)}
                            style={{ display: 'none' }}
                          />
                          <input
                            type="text"
                            readOnly
                            value={dateRange[0] ? dateRange[0].format('DD MMM YYYY') : ''}
                            placeholder="DD-MMM-YYYY"
                            onClick={() => document.getElementById('hidden-from-date')?.showPicker()}
                            style={{
                              padding: '4px 8px',
                              borderRadius: 4,
                              border: '1px solid #d9d9d9',
                              cursor: 'pointer',
                              width: '140px',
                              textAlign: 'center',
                            }}
                          />
                        </div>

                        {/* To Date */}
                        <span>To:</span>
                        <div>
                          <input
                            type="date"
                            id="hidden-to-date"
                            value={dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : ''}
                            onChange={(e) => handleDateRangeChange(1, e.target.value)}
                            min={dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined}
                            style={{ display: 'none' }}
                          />
                          <input
                            type="text"
                            readOnly
                            value={dateRange[1] ? dateRange[1].format('DD MMM YYYY') : ''}
                            placeholder="DD-MMM-YYYY"
                            onClick={() => document.getElementById('hidden-to-date')?.showPicker()}
                            style={{
                              padding: '4px 8px',
                              borderRadius: 4,
                              border: '1px solid #d9d9d9',
                              cursor: 'pointer',
                              width: '140px',
                              textAlign: 'center',
                            }}
                          />
                        </div>
                      </div>
                    </Space>
                  </Col>
                  
                  <Col xs={24} md={8} lg={8}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Text strong> Search</Text>
                      <Search
                        placeholder="Search transactions..."
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        allowClear
                      />
                    </Space>
                  </Col>
                  <Col xs={24} md={8} lg={6}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Text strong>Payment Method</Text>
                      <select
                        value={paymentMethodFilter}
                        onChange={(e) => setPaymentMethodFilter(e.target.value)}
                        style={{
                          padding: '6px 10px',
                          borderRadius: 4,
                          border: '1px solid #d9d9d9',
                        }}
                      >
                        <option value="">All</option>
                        <option value="cash">Cash</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="credit_card">Credit Card</option>
                        <option value="unpaid">Unpaid</option>
                      </select>
                    </Space>
                  </Col>

                  
                  <Col xs={24} md={8} lg={8}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Text strong>Actions</Text>
                      <Space>
                        <Button 
                          type="primary" 
                          icon={<ReloadOutlined />} 
                          onClick={fetchSalesTransactions}
                          loading={loading}
                        >
                          Refresh
                        </Button>
                        <Button type="primary" onClick={applyFilters} icon={<FilterOutlined />}> Apply</Button>
                        <Button icon={<ClearOutlined />} onClick={resetFilters}>Clear</Button>
                        <CSVLink
                          data={csvData}
                          filename={getCSVFilename()}
                          className="ant-btn ant-btn-success" 
                        >
                          <Button icon={<FileExcelOutlined />}>
                            {t("Export CSV")}
                          </Button>
                        </CSVLink>
                      </Space>
                    </Space>
                  </Col>
                </Row>
              </Card>

          <Card className="table-card">
            <Table
              columns={columns}
              dataSource={filteredTransactions}
              rowKey="transaction_id"
              loading={loading}
              pagination={{
                showSizeChanger: true,
                pageSizeOptions: ['10', '25', '50', '100'],
                defaultPageSize: 50,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} transactions`,
              }}
              scroll={{ x: 'max-content' }}
              size="middle"
            />
          </Card>

          {selectedTransaction && (
            <SalesReceiptModal
              show={!!selectedTransaction}
              onClose={() => setSelectedTransaction(null)}
              transaction={selectedTransaction}
            />
          )}
        </Content>
      </Layout>
<Modal
  title="Confirm Delete"
  open={deleteModalVisible}
  onCancel={() => {
    setDeleteModalVisible(false);
    setPasswordInput('');
    setTransactionToDelete(null);
  }}
  footer={[
    <Button 
      key="cancel" 
      onClick={() => {
        setDeleteModalVisible(false);
        setPasswordInput('');
        setTransactionToDelete(null);
      }}
    >
      Cancel
    </Button>,
    <Button 
      key="delete" 
      type="primary" 
      danger 
      loading={deleteLoading}
      onClick={handleDelete}
    >
      Delete
    </Button>,
  ]}
>
  <p>Are you sure you want to delete this sale transaction?</p>
  <p>This action cannot be undone and will return inventory items to stock.</p>
  <div style={{ marginTop: '16px' }}>
    <Text strong>Please enter your password to confirm:</Text>
    <Input.Password
      value={passwordInput}
      onChange={(e) => setPasswordInput(e.target.value)}
      placeholder="Enter your password"
      style={{ marginTop: '8px' }}
    />
  </div>
</Modal>

<Modal
  title="Change Payment Method"
  open={paymentModalVisible}
  onCancel={() => setPaymentModalVisible(false)}
  footer={[
    <Button key="cancel" onClick={() => setPaymentModalVisible(false)}>
      Cancel
    </Button>,
    <Button
      key="save"
      type="primary"
      loading={updatingPayment}
      onClick={updatePaymentMethod}
    >
      Save
    </Button>,
  ]}
>
  {paymentTransaction && (
    <>
      <p><strong>Receipt:</strong> {paymentTransaction.document_no}</p>
      <p><strong>Current Method:</strong> {paymentTransaction.payment_method}</p>

      <div style={{ marginTop: 16 }}>
        <Text strong>New Payment Method</Text>
        <select
          value={newPaymentMethod}
          onChange={(e) => setNewPaymentMethod(e.target.value)}
          style={{
            width: '100%',
            padding: '8px',
            marginTop: 6,
            borderRadius: 4,
            border: '1px solid #d9d9d9'
          }}
        >
          <option value="cash">Cash</option>
          <option value="bank_transfer">Bank Transfer</option>
          <option value="credit_card">Credit Card</option>
          <option value="unpaid">❗Unpaid</option>
        </select>
      </div>

      {/* PAYMENT DATE — add it here if it was skipped at the POS, or correct it.
          Not shown for unpaid: there is no payment to date. */}
      {newPaymentMethod !== 'unpaid' && (
        <div style={{ marginTop: 16 }}>
          <Text strong>Payment Date</Text>
          <input
            type="date"
            value={newPaymentDate}
            onChange={(e) => setNewPaymentDate(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              marginTop: 6,
              borderRadius: 4,
              border: '1px solid #d9d9d9'
            }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {paymentTransaction.payment_date
              ? 'Change the date to correct when payment was received.'
              : 'No payment date recorded yet — leave blank to set it to today.'}
          </Text>
        </div>
      )}

      {newPaymentMethod === 'bank_transfer' && (
      <div style={{ marginTop: 16 }}>
        <Text strong>Select Bank Account</Text>

        <select
          value={newBankDetailId || ''}
          onChange={(e) => setNewBankDetailId(e.target.value)}
          style={{
            width: '100%',
            padding: '8px',
            marginTop: 6,
            borderRadius: 4,
            border: '1px solid #d9d9d9'
          }}
          disabled={loadingBanks}
        >
          <option value="">Select bank...</option>

          {bankDetails.map((b) => (
            <option key={b.id} value={b.id}>
              {b.bank_name} - {b.account_number}
            </option>
          ))}
        </select>
      </div>
    )}

    </>
  )}
</Modal>

    </Layout>
  );
};

export default withRoleAccess(SalesReport, ['superadmin', 'admin']);